// src/pages/SaleInvoice.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Box, Button, Card, CardContent, Grid, Paper, Skeleton, Stack, TextField, Typography, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, IconButton,} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { saveAs } from "file-saver";

// Base URL for API requests
const API_BASE = "http://192.168.1.248:3103";

// 🔁 Converts JSON data to CSV format for download
const jsonToCsv = (rows) => {
    if (!rows.length) return "";
    const headers = Object.keys(rows[0]);
    const escape = (s) => `"${(s ?? "").toString().replace(/"/g, '""')}"`;
    const lines = [ headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")), ];
    return lines.join("\n");
};

// 🔎 Top Filter section (Search bar)
// Receives filters and handlers as props
const FilterForm = ({ filters, onChange, onSearch, onReset, isMobile }) => (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3, background: "#fafafa" }}>
        <Typography variant="h6" gutterBottom>Search Filters</Typography>
        <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center" flexWrap="wrap" >
            {/* Input fields for filters */}
            {[
                ["clientName", "Client Name"],
                ["billNo", "Bill No."],
                ["state", "State"],
                ["city", "City"],
                ["installBy", "Installed By"],
            ].map(([name, label]) => (
                <TextField key={name} name={name} label={label} value={filters[name]} onChange={onChange} size="small" fullWidth={isMobile} sx={{ flex: isMobile ? "unset" : 1 }} />
            ))}

            {/* Search / Reset buttons */}
            <Button variant="contained" color="primary" onClick={onSearch}>Search</Button>
            <Button variant="outlined" onClick={onReset}>Reset</Button>
        </Stack>
    </Paper>
);

// 🧾 Display cards for each invoice (Mobile / Responsive friendly)
const InvoiceCards = ({ rows, loading, onView, onReturn }) => {
    const avatarColors = ["#6f42c1", "#007bff", "#20c997", "#fd7e14", "#e83e8c", "#6c757d"];

    // Show skeletons while data is loading
    if (loading) {
        return (
            <Grid container spacing={2}>
                {[...Array(3)].map((_, i) => (
                    <Grid item xs={12} key={i}> <Skeleton variant="rectangular" height={140} /> </Grid>
                ))}
            </Grid>
        );
    }

    // Render invoice cards
    return (
        <Grid container spacing={2}>
            {rows.map((r, idx) => {
                const color = avatarColors[idx % avatarColors.length];
                return (
                    <Grid item xs={12} key={r.saleInvoiceId}>
                        <Card elevation={3} sx={{ p: 2, borderLeft: `5px solid ${color}`, borderRadius: 2, transition: "box-shadow 0.3s", "&:hover": { boxShadow: 6 }, }} >
                            <CardContent sx={{ pb: "16px !important" }}>
                                {/* Company Avatar + Name + Location + Status */}
                                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                                    <Box sx={{ bgcolor: color, color: "#fff", p: 1.5, borderRadius: "50%", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 16, mr: 2, }}> {r.companyName?.[0]?.toUpperCase() || "?"} </Box>

                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6" fontSize="1.1rem" noWrap>{r.companyName}</Typography>
                                        <Typography variant="body2" color="text.secondary">{r.city}, {r.state}</Typography>
                                    </Box>

                                    <Typography variant="caption" sx={{ fontWeight: 500, color: "primary.main" }}> {r.statusName} </Typography>
                                </Box>

                                {/* Invoice Info */}
                                <Grid container spacing={1} sx={{ mb: 1 }}>
                                    <Grid item xs={12} sm={4}> <Typography variant="body2"><strong>Bill No:</strong> {r.saleInvoiceNumber}</Typography> </Grid>
                                    <Grid item xs={12} sm={4}> <Typography variant="body2"><strong>Reference:</strong> {r.referenceNumber || "—"}</Typography> </Grid>
                                    <Grid item xs={12} sm={4}> <Typography variant="body2"><strong>Installed By:</strong> {r.installBy || "—"}</Typography> </Grid>
                                </Grid>

                                {/* Action Buttons */}
                                <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
                                    <Button variant="outlined" size="small" onClick={() => onView(r.saleInvoiceId)}>View</Button>
                                    <Button variant="contained" size="small" color="secondary" onClick={() => onReturn(r.saleInvoiceId)}>Return</Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
};

// ⚙️ Main SaleInvoice Component
function SaleInvoice() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // State: filter fields
    const [filters, setFilters] = useState({ clientName: "", billNo: "", state: "", city: "", installBy: "", });

    // State: invoice data, loading, and dialog
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openReturnDialog, setOpenReturnDialog] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

    // 🔃 Fetch data from API with optional filters
    const fetchData = useCallback(async (params = {}) => {
        setLoading(true);
        try {
            const { data: resp } = await axios.get(`${API_BASE}/api/get-invoice`, { params });
            setData(resp || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // 🔄 Handlers for filter input fields
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters((f) => ({ ...f, [name]: value }));
    };

    const handleSearch = () => fetchData(filters);
    const handleReset = () => {
        setFilters({ clientName: "", billNo: "", state: "", city: "", installBy: "" });
        fetchData();
    };

    // 🔍 View invoice in new tab
    const handleView = (id) => window.open(`${window.location.origin}/invoice/${id}`, `_blank`);

    // 📤 Export current data to CSV
    const handleExport = () => {
        const csv = jsonToCsv(data);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "invoices.csv");
    };

    // ↩️ Handle return actions
    const handleReturn = (id) => {
        setSelectedInvoiceId(id);
        setOpenReturnDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenReturnDialog(false);
        setSelectedInvoiceId(null);
    };

    const handleReturnAndCancel = () => {
        sendReturnRequest("cancel");
        handleCloseDialog();
    };

    const handleKeyModifiedReturn = () => {
        sendReturnRequest("return_payment");
        handleCloseDialog();
    };

    // 🚀 Send return/cancel request to backend
    const sendReturnRequest = async (actionType) => {
        try {
            await axios.post(`${API_BASE}/api/return-invoice`, { invoiceId: selectedInvoiceId, actionType, });
        } catch (err) {
            console.error("API Error:", err);
        } finally {
            handleCloseDialog();
            fetchData();
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* 🔠 Title */}
            <Typography variant="h4" gutterBottom align="center">Sale Invoices</Typography>

            {/* 🔍 Filter Form */}
            <FilterForm filters={filters} onChange={handleInputChange} onSearch={handleSearch} onReset={handleReset} isMobile={isMobile} />

            {/* 📤 Export Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Button variant="outlined" onClick={handleExport}>Export CSV</Button>
            </Box>

            {/* 📋 Invoice Cards */}
            <InvoiceCards rows={data} loading={loading} onView={handleView} onReturn={handleReturn} />

            {/* 🔄 Return Options Dialog */}
            <Dialog open={openReturnDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4, p: 2, boxShadow: 8, backgroundColor: theme.palette.background.paper, transition: "all 0.3s ease-in-out", }, }} >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0, }}>
                    <Typography fontWeight={600}>Return Options</Typography>
                    <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 3, pb: 3 }}>
                    <Stack direction={isMobile ? "column" : "row"} spacing={2} justifyContent="center">
                        <Button variant="outlined" color="error" onClick={handleReturnAndCancel} fullWidth> Return & Cancel </Button>
                        <Button variant="contained" color="primary" onClick={handleKeyModifiedReturn} fullWidth> Key Modified & Return Payment </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default SaleInvoice;